import React, { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';

import './Appointments.css';

import { doctorListPatients, doctorCreateAppointment, doctorListSessions, doctorListAppointments } from './api';
import { createWebSocketConnection } from '../utils/webrtcConfig';


const timeSlots = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30"
];

const AppointmentModal = memo(({
  editingAppointment,
  formData,
  setFormData,
  patientSearch,
  setPatientSearch,
  showPatientDropdown,
  setShowPatientDropdown,
  filteredPatients,
  selectedPatient,
  setSelectedPatient,
  handleSubmit,
  setShowModal,
  loading
}) => {
  const patientInputRef = useRef(null);
  const purposeRef = useRef(null);
  const notesRef = useRef(null);

  // Keep focus on the field user is typing even if parent re-renders
  useEffect(() => {
    if (!showPatientDropdown) return;
    if (document.activeElement === patientInputRef.current) return;
    patientInputRef.current?.focus({ preventScroll: true });
    const node = patientInputRef.current;
    if (node) {
      const len = node.value.length;
      try { node.setSelectionRange(len, len); } catch { }
    }
  }, [showPatientDropdown]);

  const handlePatientSearchChange = useCallback((e) => {
    setPatientSearch(e.target.value);
    setShowPatientDropdown(true);
  }, [setPatientSearch, setShowPatientDropdown]);

  const handlePurposeChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, purpose: e.target.value }));
  }, [setFormData]);

  const handleNotesChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, notes: e.target.value }));
  }, [setFormData]);

  const handlePatientSelect = useCallback((patient) => {
    setSelectedPatient(patient);
    setPatientSearch(patient.name);
    setFormData(prev => ({ ...prev, patientId: patient.id }));
    setShowPatientDropdown(false);
    // after selecting, move focus to purpose field
    setTimeout(() => purposeRef.current?.focus(), 0);
  }, [setSelectedPatient, setPatientSearch, setFormData, setShowPatientDropdown]);

  return (
    <div className="modal-overlay" onClick={() => setShowModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingAppointment ? 'Edit Appointment' : 'New Appointment'}</h2>
          <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="appointment-form">
          <div className="form-group">
            <label>Patient:</label>
            <div className="searchable-dropdown">
              <input
                ref={patientInputRef}
                type="text"
                placeholder="Search patients..."
                value={patientSearch}
                onChange={handlePatientSearchChange}
                onFocus={() => setShowPatientDropdown(true)}
                autoComplete="off"
              />
              {showPatientDropdown && (
                <div className="dropdown-options">
                  {loading ? (
                    <div className="dropdown-item">Loading patients...</div>
                  ) : filteredPatients.length > 0 ? (
                    filteredPatients.map(patient => (
                      <div
                        key={patient.id}
                        className="dropdown-item"
                        onClick={() => handlePatientSelect(patient)}
                      >
                        <div className="patient-name">{patient.name}</div>
                        <div className="patient-contact">{patient.contact}</div>
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-item">No patients found</div>
                  )}
                </div>
              )}
            </div>
            {selectedPatient && (
              <div className="selected-patient">
                <div className="patient-name">{selectedPatient.name}</div>
                <div className="patient-contact">{selectedPatient.contact}</div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Date:</label>
            <input
              type="date"
              value={formData.date}
              onChange={e => setFormData(prev => ({ ...prev, date: e.target.value }))}
              required
            />
          </div>

          <div className="form-group">
            <label>Time:</label>
            <select
              value={formData.time}
              onChange={e => setFormData(prev => ({ ...prev, time: e.target.value }))}
              required
            >
              <option value="">Select time</option>
              {timeSlots.map(slot => (
                <option key={slot} value={slot}>{slot}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Duration (minutes):</label>
            <input
              type="number"
              min="15"
              max="180"
              step="15"
              value={formData.duration}
              onChange={e => setFormData(prev => ({ ...prev, duration: Number(e.target.value) }))}
              required
            />
          </div>

          <div className="form-group">
            <label>Purpose:</label>
            <textarea
              ref={purposeRef}
              rows="2"
              placeholder="Reason for consultation"
              value={formData.purpose}
              onChange={handlePurposeChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Notes:</label>
            <textarea
              ref={notesRef}
              rows="3"
              placeholder="Additional details or notes"
              value={formData.notes}
              onChange={handleNotesChange}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (editingAppointment ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default function Appointments({ refreshTrigger, onAppointmentCreated, newAppointmentIds: propNewAppointmentIds, setNewAppointmentIds: propSetNewAppointmentIds }) {
  const [appointments, setAppointments] = useState([]);
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [formData, setFormData] = useState({
    patientId: '',
    date: '',
    time: '',
    purpose: '',
    duration: 60,
    notes: ''
  });



  // Real data from database

  const [patients, setPatients] = useState([]);

  const [loading, setLoading] = useState(true);

  const [doctorId, setDoctorId] = useState(null);



  // Searchable dropdown states

  const [patientSearch, setPatientSearch] = useState('');

  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState(null);

  // Use props if provided, otherwise use local state (for backward compatibility)
  const [localNewAppointmentIds, setLocalNewAppointmentIds] = useState(new Set());
  const newAppointmentIds = propNewAppointmentIds !== undefined ? propNewAppointmentIds : localNewAppointmentIds;
  const setNewAppointmentIds = propSetNewAppointmentIds !== undefined ? propSetNewAppointmentIds : setLocalNewAppointmentIds;

  // Ref to store the latest loadAppointments function to avoid dependency issues
  const loadAppointmentsRef = useRef(null);

  // Load patients and appointments on component mount

  useEffect(() => {

    const id = localStorage.getItem('neuro_user_id');

    setDoctorId(id);

    if (id) {

      loadPatients(id);

      loadAppointments(id);

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Reload appointments when refreshTrigger changes

  useEffect(() => {

    if (doctorId && refreshTrigger) {

      loadAppointments(doctorId);

    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, doctorId]);

  // Track previous appointment IDs size to detect new notifications
  const prevAppointmentIdsSizeRef = useRef(0);

  // Reload appointments when new appointment notification is received (if props are provided, WebSocket is handled by parent)
  useEffect(() => {
    if (propNewAppointmentIds === undefined) {
      // Fallback: Set up WebSocket connection if props not provided (backward compatibility)
      if (!doctorId) return;

      let userWs = null;
      let reconnectInterval = null;

      const connectWebSocket = async () => {
        try {
          userWs = await createWebSocketConnection(`user/${doctorId}`);
          
          userWs.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              if (data.type === 'new_appointment') {
                const appointmentId = String(data.appointment_id);
                setNewAppointmentIds(prev => new Set([...prev, appointmentId]));
                if (loadAppointmentsRef.current) {
                  loadAppointmentsRef.current(doctorId);
                }
              }
            } catch (e) {
              // WebSocket message parse error
            }
          };

          userWs.onclose = () => {
            if (!reconnectInterval) {
              reconnectInterval = setInterval(connectWebSocket, 5000);
            }
          };

          userWs.onerror = () => {
            userWs?.close();
          };
        } catch (e) {
          console.error('❌ [Appointments] Failed to connect WebSocket:', e);
          if (!reconnectInterval) {
            reconnectInterval = setInterval(connectWebSocket, 5000);
          }
        }
      };

      connectWebSocket();

      return () => {
        if (userWs) {
          try { userWs.close(); } catch {}
        }
        if (reconnectInterval) {
          clearInterval(reconnectInterval);
        }
      };
    }
  }, [doctorId, propNewAppointmentIds, setNewAppointmentIds]);

  // Separate effect to reload appointments when new appointment IDs are added (when using props)
  useEffect(() => {
    if (propNewAppointmentIds !== undefined) {
      // Props provided: WebSocket is handled by parent, reload when new appointment IDs are added
      const currentSize = newAppointmentIds.size;
      if (currentSize > prevAppointmentIdsSizeRef.current && doctorId && loadAppointmentsRef.current) {
        loadAppointmentsRef.current(doctorId);
      }
      prevAppointmentIdsSizeRef.current = currentSize;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, propNewAppointmentIds, newAppointmentIds.size]);



  // Close dropdown when clicking outside

  useEffect(() => {

    const handleClickOutside = (event) => {

      // Only close dropdown if clicking outside the searchable dropdown

      // Don't interfere with form inputs

      if (showPatientDropdown &&

        !event.target.closest('.searchable-dropdown')
      ) {

        setShowPatientDropdown(false);

      }

    };



    document.addEventListener('mousedown', handleClickOutside);

    return () => {

      document.removeEventListener('mousedown', handleClickOutside);

    };

  }, [showPatientDropdown]);



  // Load appointments from database

  const loadAppointments = useCallback(async (doctor_id) => {

    try {

      const apiRes = await doctorListAppointments(doctor_id);

      if (apiRes.status_code === 200) {

        // Transform database appointments to match frontend format

        const transformedAppointments = apiRes.data.map(apt => ({

          id: String(apt.id), // Ensure ID is always a string for consistent comparison

          patientId: apt.patient_id,

          patientName: apt.patient_name,

          date: apt.date,

          time: apt.time,

          purpose: apt.purpose || '',

          duration: apt.duration || 60,

          notes: apt.notes || '',

          status: 'upcoming',

          is_outpatient: !!apt.is_outpatient,

          outpatientUserId: apt.outpatient_user_id || null


        }));



        // 1) Remove appointments whose time has already passed

        const now = new Date();

        const upcomingOnly = transformedAppointments.filter(apt => {

          try {

            const dt = new Date(`${apt.date}T${apt.time}:00`);

            return dt.getTime() > now.getTime();

          } catch { return false; }

        });



        // 2) Remove appointments that already have a completed session near the scheduled time

        //    (same-day and within -15 to +90 minutes window)

        const uniquePatientIds = Array.from(new Set(upcomingOnly.map(a => a.patientId)));

        const sessionsByPatient = {};

        // Fetch sessions per patient once

        await Promise.all(uniquePatientIds.map(async (pid) => {

          try {

            const sRes = await doctorListSessions(pid);

            if (sRes.status_code === 200 && Array.isArray(sRes.data)) {

              sessionsByPatient[pid] = sRes.data

                .filter(s => !!s.session_date)

                .map(s => new Date(s.session_date));

            } else {

              sessionsByPatient[pid] = [];

            }

          } catch {

            sessionsByPatient[pid] = [];

          }

        }));



        const isSameDay = (a, b) => (

          a.getFullYear() === b.getFullYear() &&

          a.getMonth() === b.getMonth() &&

          a.getDate() === b.getDate()

        );



        const kept = upcomingOnly.filter(apt => {

          const dt = new Date(`${apt.date}T${apt.time}:00`);

          const sess = sessionsByPatient[apt.patientId] || [];

          const hasNearbySession = sess.some(sdt => {

            if (!isSameDay(sdt, dt)) return false;

            const diffMin = (sdt.getTime() - dt.getTime()) / 60000;

            return diffMin >= -15 && diffMin <= 90;

          });

          return !hasNearbySession;

        });



        setAppointments(kept);

      }

    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  }, []);

  // Update ref whenever loadAppointments changes
  useEffect(() => {
    loadAppointmentsRef.current = loadAppointments;
  }, [loadAppointments]);

  const loadPatients = async (doctor_id) => {

    try {

      setLoading(true);

      const response = await doctorListPatients(doctor_id);

      if (response.status_code === 200) {

        setPatients(response.data);

      }

    } catch (error) {

      console.error('Error loading patients:', error);

    } finally {

      setLoading(false);

    }

  };



  // Filter patients based on search

  const filteredPatients = patients.filter(patient =>

    patient.name.toLowerCase().includes(patientSearch.toLowerCase()) ||

    patient.contact?.includes(patientSearch)

  );

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const formatDate = (date) => {
    // Use local date parts to avoid UTC day-shift (which caused dots to appear on the next day)
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const _formatTime = (time) => {
    return time.substring(0, 5);
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = formatDate(date);
    return appointments.filter(apt => {
      if (!apt || !apt.date) return false;
      // Normalize any incoming shape to local YYYY-MM-DD to avoid TZ/day-shift bugs
      let aptStr = '';
      if (typeof apt.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(apt.date)) {
        aptStr = apt.date;
      } else {
        try {
          const d = new Date(apt.date);
          aptStr = formatDate(d);
        } catch { aptStr = String(apt.date); }
      }
      return aptStr === dateStr;
    });
  };

  const getAppointmentsForStatus = (status) => {
    if (status === 'all') return appointments;
    return appointments.filter(apt => apt.status === status);
  };

  const filteredAppointments = useMemo(() =>
    getAppointmentsForStatus(selectedStatus),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointments, selectedStatus]
  );

  const handleAddAppointment = () => {
    setEditingAppointment(null);
    setFormData({
      patientId: '',
      date: formatDate(new Date()),
      time: '10:00',
      purpose: '',
      duration: 60,
      notes: ''
    });
    setSelectedPatient(null);
    setPatientSearch('');
    setShowPatientDropdown(false);
    setShowModal(true);
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      patientId: appointment.patientId,
      date: appointment.date,
      time: appointment.time,
      purpose: appointment.purpose,
      duration: appointment.duration,
      notes: appointment.notes || ''
    });
    const matchedPatient = patients.find(p => p.id === appointment.patientId);
    if (matchedPatient) {
      setSelectedPatient(matchedPatient);
      setPatientSearch(matchedPatient.name);
    } else {
      setSelectedPatient(null);
      setPatientSearch('');
    }
    setShowPatientDropdown(false);
    setShowModal(true);
  };

  const handleCancelAppointment = (appointmentId) => {
    setAppointments(prev =>
      prev.map(apt =>
        apt.id === appointmentId
          ? { ...apt, status: 'cancelled' }
          : apt
      )
    );
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Check for double booking
    const conflictingAppointment = appointments.find(apt =>
      apt.id !== editingAppointment?.id &&
      apt.date === formData.date &&
      apt.time === formData.time &&
      apt.status !== 'cancelled'
    );

    if (conflictingAppointment) {
      alert('Error: You already have an appointment at this time. Please choose a different time.');
      return;
    }

    try {

      if (editingAppointment) {

        // Update existing appointment (for now, just update local state)

        setAppointments(prev =>

          prev.map(apt =>

            apt.id === editingAppointment.id

              ? { ...apt, ...formData, patientName: selectedPatient?.name }

              : apt

          )

        );

      } else {

        // Create new appointment in database

        if (selectedPatient && doctorId) {

          const response = await doctorCreateAppointment(

            doctorId,

            selectedPatient.id,

            formData.date,

            formData.time,

            formData.purpose,

            formData.duration,

            formData.notes

          );



          if (response.status_code === 200) {

            // Reload appointments from database to get the latest data

            await loadAppointments(doctorId);

            alert('Appointment created successfully!');

            // Notify parent (DoctorDashboard) to refresh patients so Next Session updates immediately
            try { if (typeof onAppointmentCreated === 'function') onAppointmentCreated(); } catch { }

          } else {

            alert('Failed to create appointment. Please try again.');

            return;

          }

        }

      }



      setShowModal(false);

      setEditingAppointment(null);

      setSelectedPatient(null);

      setPatientSearch('');

    } catch (error) {
      console.error('Error saving appointment:', error);
      alert('Failed to save appointment. Please try again.');
    }
  }, [appointments, editingAppointment, formData, selectedPatient, doctorId, loadAppointments, onAppointmentCreated]);

  const CalendarView = () => {
    const days = viewMode === 'month' ? getDaysInMonth(selectedDate) : getWeekDays(selectedDate);

    return (
      <div className="calendar-view">
        <div className="calendar-header">
          <button onClick={() => {
            const newDate = new Date(selectedDate);
            if (viewMode === 'month') {
              newDate.setMonth(newDate.getMonth() - 1);
            } else {
              newDate.setDate(newDate.getDate() - 7);
            }
            setSelectedDate(newDate);
          }}>
            ← Previous
          </button>
          <h3>
            {viewMode === 'month'
              ? selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
              : `Week of ${days[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            }
          </h3>
          <button onClick={() => {
            const newDate = new Date(selectedDate);
            if (viewMode === 'month') {
              newDate.setMonth(newDate.getMonth() + 1);
            } else {
              newDate.setDate(newDate.getDate() + 7);
            }
            setSelectedDate(newDate);
          }}>
            Next →
          </button>
        </div>

        <div className="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}

          {days.map((day, index) => {
            if (!day) return <div key={index} className="calendar-day empty"></div>;

            const dayAppointments = getAppointmentsForDate(day);
            const isToday = formatDate(day) === formatDate(new Date());

            return (
              <div
                key={index}
                className={`calendar-day ${isToday ? 'today' : ''}`}
                onClick={() => setSelectedDate(day)}
              >
                <div className="day-number">{day.getDate()}</div>
                <div className="day-appointments">
                  {dayAppointments.slice(0, 2).map(apt => (
                    <div
                      key={apt.id}
                      className={`appointment-dot ${apt.status}`}
                      title={`${apt.time} - ${apt.patientName}`}
                    ></div>
                  ))}
                  {dayAppointments.length > 2 && (
                    <div className="more-appointments">+{dayAppointments.length - 2}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="appointments-container">
      <div className="appointments-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2>Appointments & Scheduling</h2>
          {newAppointmentIds.size > 0 && (
            <span className="notification-count-badge" title={`${newAppointmentIds.size} new appointment(s)`}>
              {newAppointmentIds.size}
            </span>
          )}
        </div>
        <button className="btn-primary" onClick={handleAddAppointment}>
          + New Appointment
        </button>
      </div>

      <div className="appointments-controls">
        <div className="view-controls">
          <button
            className={`view-btn ${viewMode === 'month' ? 'active' : ''}`}
            onClick={() => setViewMode('month')}
          >
            Month
          </button>
          <button
            className={`view-btn ${viewMode === 'week' ? 'active' : ''}`}
            onClick={() => setViewMode('week')}
          >
            Week
          </button>
          <button
            className={`view-btn ${viewMode === 'day' ? 'active' : ''}`}
            onClick={() => setViewMode('day')}
          >
            Day
          </button>
        </div>

        <div className="filter-controls">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Appointments</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="appointments-content">
        <div className="calendar-section">
          <CalendarView />
        </div>

        <div className="appointments-list">
          <h3>Appointments List</h3>
          <div className="appointments-table">
            {filteredAppointments.length === 0 ? (
              <div className="no-appointments">
                No appointments found for the selected criteria.
              </div>
            ) : (
              filteredAppointments.map(appointment => {
                const appointmentIdStr = String(appointment.id);
                const isNew = newAppointmentIds.has(appointmentIdStr);
                if (isNew) {
                  // new appointment detected
                }
                return (
                <div 
                  key={appointment.id} 
                  className={`appointment-item ${appointment.status} ${isNew ? 'new-appointment' : ''}`}
                  onClick={() => {
                    // Mark as read when clicked
                    if (isNew) {
                      setNewAppointmentIds(prev => {
                        const next = new Set(prev);
                        next.delete(String(appointment.id));
                        return next;
                      });
                    }
                  }}
                >
                  <div className="appointment-info">
                    <div className="appointment-header">
                      <h4>{appointment.patientName}</h4>
                      {isNew && (
                        <span className="notification-badge" title="New appointment">
                          🔔 New
                        </span>
                      )}
                      <span className={`status-badge ${appointment.status}`}>
                        {appointment.status}
                      </span>
                      {appointment.is_outpatient && (

                        <span className="status-badge" style={{ marginLeft: 8, background: '#0ea5e9', color: '#ffffff' }}>

                          Outpatient

                        </span>

                      )}
                    </div>
                    <div className="appointment-details">
                      <p><strong>Date:</strong> {new Date(appointment.date).toLocaleDateString()}</p>
                      <p><strong>Time:</strong> {appointment.time} ({appointment.duration} min)</p>
                      <p><strong>Purpose:</strong> {appointment.purpose}</p>
                      {appointment.notes && (
                        <p><strong>Notes:</strong> {appointment.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="appointment-actions">
                    {appointment.status === 'upcoming' && (
                      <>
                        <button
                          className="btn-edit"
                          onClick={() => handleEditAppointment(appointment)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-cancel"
                          onClick={() => handleCancelAppointment(appointment.id)}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {appointment.status === 'upcoming' && (
                      <button className="btn-reminder">
                        Send Reminder
                      </button>
                    )}
                    {appointment.is_outpatient && appointment.status === 'upcoming' && (

                      <button className="btn-primary" style={{ marginLeft: 8 }} onClick={() => {

                        try {

                          const roomId = `apt_${appointment.id}`;

                          window.dispatchEvent(new CustomEvent('openDoctorSession', { detail: { patientId: appointment.patientId, roomId, outpatientUserId: appointment.outpatientUserId } }));

                        } catch { }

                      }}>

                        Video Call

                      </button>

                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <AppointmentModal
          editingAppointment={editingAppointment}
          formData={formData}
          setFormData={setFormData}
          patientSearch={patientSearch}
          setPatientSearch={setPatientSearch}
          showPatientDropdown={showPatientDropdown}
          setShowPatientDropdown={setShowPatientDropdown}
          filteredPatients={filteredPatients}
          selectedPatient={selectedPatient}
          setSelectedPatient={setSelectedPatient}
          handleSubmit={handleSubmit}
          setShowModal={setShowModal}
          loading={loading}
        />
      )}
    </div>
  );
}
